import * as React from 'react'
import { cn } from '@/lib/utils'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, maxLength, onChange, onPaste, ...props }, ref) => {
    const [isAtMaxLength, setIsAtMaxLength] = React.useState(false)
    const [isBlinking, setIsBlinking] = React.useState(false)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value

      // If maxLength is set and value reaches it
      if (maxLength && value.length >= maxLength) {
        // Only trigger blink if we're at max and trying to add more
        if (value.length === maxLength && !isAtMaxLength) {
          setIsAtMaxLength(true)
          setIsBlinking(true)
          // Stop blinking after animation completes (3 blinks * 0.5s = 1.5s)
          setTimeout(() => {
            setIsBlinking(false)
          }, 1500)
        }
        // Truncate to maxLength
        e.target.value = value.slice(0, maxLength)
      } else {
        // Reset state if user deletes characters
        if (isAtMaxLength) {
          setIsAtMaxLength(false)
        }
      }

      onChange?.(e)
    }

    // Handle paste events to enforce maxLength
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      onPaste?.(e)
      if (e.defaultPrevented) return
      if (!maxLength) return

      const input = e.currentTarget
      const currentValue = input.value
      const selectionStart = input.selectionStart || 0
      const selectionEnd = input.selectionEnd || 0
      const pastedText = e.clipboardData.getData('text')

      // Calculate what the new value would be
      const beforeSelection = currentValue.slice(0, selectionStart)
      const afterSelection = currentValue.slice(selectionEnd)
      const newValue = beforeSelection + pastedText + afterSelection

      if (newValue.length > maxLength) {
        e.preventDefault()
        // Truncate pasted content to fit
        const availableSpace = maxLength - beforeSelection.length - afterSelection.length
        const truncatedPaste = pastedText.slice(0, Math.max(0, availableSpace))

        // Manually set the value
        const finalValue = beforeSelection + truncatedPaste + afterSelection
        input.value = finalValue

        // Trigger onChange with the truncated value
        const syntheticEvent = {
          ...e,
          target: input,
          currentTarget: input,
        } as React.ChangeEvent<HTMLInputElement>

        setIsAtMaxLength(true)
        setIsBlinking(true)
        setTimeout(() => {
          setIsBlinking(false)
        }, 1500)

        onChange?.(syntheticEvent)
      }
    }

    return (
      <input
        type={type}
        maxLength={maxLength}
        className={cn(
          'flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-error focus-visible:ring-error',
          isBlinking && 'animate-max-length-blink',
          isAtMaxLength && !isBlinking && 'border-warning-persistent',
          className
        )}
        ref={ref}
        onChange={handleChange}
        onPaste={handlePaste}
        {...props}
      />
    )
  }
)
Input.displayName = 'Input'

export { Input }
